import { CircleAlert, CircleCheck, TriangleAlert } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";

import { type ShowToast, ToastContext, type ToastKind } from "./toastContext";

export const TOAST_DURATION_MS = 3200;

const ICONS = {
  ok: { icon: CircleCheck, className: "text-highlight" },
  warn: { icon: TriangleAlert, className: "text-warning" },
  error: { icon: CircleAlert, className: "text-danger" },
} as const;

interface ToastState {
  id: number;
  text: string;
  kind: ToastKind;
}

/** Un aviso a la vez, abajo al centro. El nuevo reemplaza al anterior. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null);
  const counter = useRef(0);

  const show = useCallback<ShowToast>((text, kind = "ok") => {
    counter.current += 1;
    setToast({ id: counter.current, text, kind });
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), TOAST_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const { icon: Icon, className } = ICONS[toast?.kind ?? "ok"];

  return (
    <ToastContext.Provider value={show}>
      {children}
      {/* La región existe siempre: un `role=status` que aparece junto con su
          texto no siempre se anuncia. */}
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-7 z-[120] flex justify-center px-4"
      >
        {toast && (
          <div
            key={toast.id}
            className="bg-surface-raised border-border-strong rounded-box shadow-popover animate-pop-in flex max-w-[min(520px,100%)] items-center gap-2.5 border py-3 pr-4 pl-3.5 text-sm"
          >
            <Icon
              aria-hidden
              strokeWidth={1.75}
              className={cn("size-[17px] shrink-0", className)}
            />
            <span>{toast.text}</span>
          </div>
        )}
      </div>
    </ToastContext.Provider>
  );
}
